import React, { useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { DropdownAction } from "../../../../../components/Dropdown/types";
import { DeletePopUp } from "../../../../../components/PopUp";
import { useResponsive } from "../../../../../hooks/useResponsive";
import { PAGE_SIZE } from "../../../../../utils/constants";
import { SECTION_CARD_BREAKPOINT } from "../../constants";
import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import { InvestmentTransferItem } from "../../../../../services/investments/types";
import { useInvestmentContributions } from "../../hooks";
import { ContributionRow } from "./ContributionRow";
import { ContributionCard } from "./ContributionCard";
import { ContributionsProps } from "./types";
import {
  ContributionsCard,
  HeaderRow,
  HeaderToggle,
  Chevron,
  TitleBlock,
  Title,
  Subtitle,
  Badge,
  Body,
  Table,
  TableHead,
  HeaderCell,
  CardList,
  Footer,
  FooterText,
  Message,
  SkeletonRow,
  SkeletonLine,
  LoadMoreLabel,
} from "./styles";
import Button, { ButtonTypes } from "../../../../../components/Button";
import { SectionRetryButton } from "../../../../../components/SectionRetryButton";
import { KeyboardArrowDownRounded } from "@mui/icons-material";

const SKELETON_ROWS = [0, 1, 2, 3, 4];

const ContributionsComponent: React.FC<ContributionsProps> = ({
  id,
  onChanged,
  onEdit,
  onDuplicate,
}) => {
  const isMobile = useResponsive(SECTION_CARD_BREAKPOINT);
  const [isExpanded, setIsExpanded] = useState(false);
  const [pendingDelete, setPendingDelete] =
    useState<InvestmentTransferItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    items,
    totalRows,
    hasNext,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
    removeContribution,
    reload,
  } = useInvestmentContributions({
    id,
    onChanged,
  });

  const shownCount = items.length;
  const remaining = Math.max(totalRows - shownCount, 0);
  const loadCount = Math.min(PAGE_SIZE, remaining);
  const hasItems = shownCount > 0;

  const badgeText = isMobile
    ? `${totalRows} contributions`
    : `${shownCount} of ${totalRows} contributions`;
  const footerText = isMobile
    ? `${shownCount} of ${totalRows} · newest first`
    : `Showing ${shownCount} of ${totalRows} · newest first`;
  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const buildRowActions = (item: InvestmentTransferItem): DropdownAction[] => [
    {
      id: "edit",
      label: "Edit contribution",
      icon: <EditIcon fontSize="small" />,
      onClick: () => onEdit?.(item),
      show: Boolean(onEdit),
    },
    {
      id: "duplicate",
      label: "Duplicate contribution",
      icon: <ContentCopyIcon fontSize="small" />,
      onClick: () => onDuplicate?.(item),
      show: Boolean(onDuplicate),
    },
    {
      id: "delete",
      label: "Delete contribution",
      icon: <DeleteIcon fontSize="small" />,
      onClick: () => setPendingDelete(item),
      variant: "destructive",
      show: true,
    },
  ];

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setPendingDelete(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    setIsDeleting(true);
    await removeContribution(pendingDelete);
    setIsDeleting(false);
    setPendingDelete(null);
  };

  let body: React.ReactNode;
  if (isLoading && !hasItems) {
    body = (
      <Table>
        {SKELETON_ROWS.map((key) => (
          <SkeletonRow key={key}>
            <SkeletonLine $width="30%" />
            <SkeletonLine $width="20%" />
          </SkeletonRow>
        ))}
      </Table>
    );
  } else if (error && !hasItems) {
    body = (
      <>
        <Message>{error}</Message>
        <SectionRetryButton onClick={() => void reload()} />
      </>
    );
  } else if (!hasItems) {
    body = <Message>No contributions logged yet.</Message>;
  } else if (isMobile) {
    body = (
      <CardList>
        {items.map((item) => (
          <ContributionCard
            key={item.id}
            item={item}
            actions={buildRowActions(item)}
          />
        ))}
      </CardList>
    );
  } else {
    body = (
      <Table>
        <TableHead>
          <HeaderCell>Date</HeaderCell>
          <HeaderCell>Change</HeaderCell>
          <HeaderCell>Invested</HeaderCell>
          <HeaderCell>Portfolio Value</HeaderCell>
          <HeaderCell aria-hidden="true" />
        </TableHead>
        {items.map((item) => (
          <ContributionRow
            key={item.id}
            item={item}
            actions={buildRowActions(item)}
          />
        ))}
      </Table>
    );
  }

  const showFooter = isExpanded && hasItems;
  let deleteText: React.ReactNode = "";
  if (pendingDelete) {
    deleteText = (
      <>
        Do you really want to delete the contribution of{" "}
        <strong>{formatCurrency(Math.abs(pendingDelete.amount))}</strong> from{" "}
        <strong>{formatDate(pendingDelete.date, DateFormat.DMY_SPACE)}</strong>?
      </>
    );
  }

  return (
    <ContributionsCard aria-label="Investment contributions">
      <HeaderRow>
        <HeaderToggle
          type="button"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          aria-label={
            isExpanded ? "Collapse contributions" : "Expand contributions"
          }
        >
          <Chevron $expanded={isExpanded}>
            <KeyboardArrowDownIcon fontSize="small" />
          </Chevron>
          <TitleBlock>
            <Title>Contributions</Title>
            <Subtitle $expanded={isExpanded}>Logged transfers</Subtitle>
          </TitleBlock>
        </HeaderToggle>
        <Badge>{badgeText}</Badge>
      </HeaderRow>

      {isExpanded && <Body>{body}</Body>}

      {showFooter && (
        <Footer>
          <FooterText>{footerText}</FooterText>
          {hasNext && (
            <Button
              type={ButtonTypes.primary}
              onClick={loadMore}
              disabled={isLoadingMore}
            >
              <LoadMoreLabel>
                {isLoadingMore ? "Loading…" : `Load ${loadCount} more`}
                <KeyboardArrowDownRounded fontSize="small" />
              </LoadMoreLabel>
            </Button>
          )}
        </Footer>
      )}

      {pendingDelete && (
        <DeletePopUp
          isOpen={Boolean(pendingDelete)}
          setIsOpen={closeDeleteModal}
          title="Delete Contribution"
          text={deleteText}
          isLoading={isDeleting}
          handleOnDelete={() => void handleConfirmDelete()}
        />
      )}
    </ContributionsCard>
  );
};

export const Contributions = React.memo(ContributionsComponent);
